/** Shared compact match-card tokens (Matches page / compact hub layout). */

/** Fixed compact card shell — header 42 + body 121 (60+1+60) + footer 60 = 223px. */
export const MATCH_CARD_COMPACT_CARD_HEIGHT_PX = 223;
export const MATCH_CARD_COMPACT_HEADER_HEIGHT_PX = 42;
export const MATCH_CARD_COMPACT_BODY_HEIGHT_PX = 121;
export const MATCH_CARD_COMPACT_BODY_SIZE_CLASS =
    "h-[121px] min-h-[121px] max-h-[121px]";
export const MATCH_CARD_COMPACT_FOOTER_HEIGHT_PX = 60;

export const MATCH_CARD_COMPACT_CARD_SIZE_CLASS = "h-[223px] min-h-[223px] max-h-[223px]";

/** Horses — single 60px runners band + divider (no second metadata row). 42 + 61 + 60 = 163px. */
export const MATCH_CARD_COMPACT_HORSE_BODY_HEIGHT_PX = 61;
export const MATCH_CARD_COMPACT_HORSE_BODY_SIZE_CLASS =
    "h-[61px] min-h-[61px] max-h-[61px]";
export const MATCH_CARD_COMPACT_HORSE_CARD_HEIGHT_PX = 163;
export const MATCH_CARD_COMPACT_HORSE_CARD_SIZE_CLASS =
    "h-[163px] min-h-[163px] max-h-[163px]";

export const MATCH_CARD_COMPACT_HEADER_ROW_CLASS =
    "flex h-[42px] min-h-[42px] max-h-[42px] items-stretch justify-between gap-2";

export const MATCH_CARD_COMPACT_SPORT_ICON_WRAP_CLASS =
    "flex min-w-0 flex-1 items-center gap-1.5 pl-2";

export const MATCH_CARD_COMPACT_SPORT_ICON_SIZE_CLASS = "h-[21px] w-[21px] shrink-0";

export const MATCH_CARD_COMPACT_COUNTRY_FLAG_CLASS =
    "flex shrink-0 items-center text-[18px] leading-none";

export const MATCH_CARD_COMPACT_HEADER_COMPETITION_TEXT_CLASS =
    "min-w-0 flex-1 truncate font-chakra text-[12px] font-normal leading-none tracking-normal normal-case text-white";

export const MATCH_CARD_COMPACT_STATUS_WRAP_CLASS = "flex flex-shrink-0 items-center gap-1.5 pl-2 pr-3";

export const MATCH_CARD_COMPACT_STATUS_TEXT_CLASS =
    "whitespace-nowrap text-right font-chakra text-[12px] font-normal uppercase leading-[100%] tracking-normal";

/**
 * User-facing match status copy (ads / compliance): keep internal tokens as IN-PLAY /
 * BREAK IN PLAY for logic; map only at display time.
 * Horses live status → RACE LIVE; other sports → MATCH LIVE.
 */
export function formatMatchStatusForDisplay(
    text: string,
    options?: { sport?: string | null },
): string {
    const trimmed = String(text ?? "").trim();
    if (!trimmed) return trimmed;
    const upper = trimmed.toUpperCase();
    const sport = String(options?.sport ?? "").toLowerCase();
    const isHorses = sport === "horses" || sport === "horse" || sport === "racing";
    if (upper === "RACING" || upper === "RUNNING") {
        return "RACE LIVE";
    }
    if (upper === "IN-PLAY" || upper === "IN PLAY") {
        return isHorses ? "RACE LIVE" : "MATCH LIVE";
    }
    const inPlayPrefix = /^(in[-\s]?play)\b/i;
    if (inPlayPrefix.test(trimmed) && !upper.startsWith("BREAK IN PLAY")) {
        const rest = trimmed.replace(inPlayPrefix, "").trim();
        const live = isHorses ? "RACE LIVE" : "MATCH LIVE";
        return rest ? `${live} ${rest}` : live;
    }
    if (upper.startsWith("BREAK IN PLAY")) {
        return trimmed.replace(/break\s+in\s+play/gi, "MATCH BREAK");
    }
    return trimmed;
}

/** Matches hub header — live status reads as MATCH LIVE; keep other labels as-is. */
export function formatCompactMatchCardStatusText(text: string): string {
    const trimmed = text.trim();
    if (!trimmed) return trimmed;
    return formatMatchStatusForDisplay(trimmed);
}

export const MATCH_CARD_COMPACT_STATUS_SENTENCE_CLASS =
    "whitespace-nowrap text-right font-chakra text-[12px] font-normal normal-case leading-[100%] tracking-normal";

export function isFootballUpcomingListStatus(text: string): boolean {
    const normalized = text.trim().toUpperCase();
    return (
        normalized.startsWith("STARTS IN ") ||
        normalized.startsWith("IN ") ||
        normalized === "STARTING SOON"
    );
}

export function isCompactMatchCardStartsInStatus(text: string): boolean {
    const normalized = text.trim().toUpperCase();
    return normalized.startsWith("STARTS IN ") || /^IN\s+\d/.test(normalized);
}

export const MATCH_CARD_COMPACT_STARTS_IN_STATUS_CLASS = "text-[#32F78D]";

/** Football listings — sentence-case “Starts in 3h 40m” / “in 1m” / “Starting soon”. */
export function formatFootballListCardStatusText(text: string): string {
    const trimmed = text.trim();
    if (!trimmed) return trimmed;
    if (trimmed.toUpperCase() === "IN-PLAY" || trimmed.toUpperCase() === "IN PLAY") {
        return "MATCH LIVE";
    }
    if (trimmed.toUpperCase().startsWith("STARTS IN ")) {
        return `Starts in ${trimmed.slice("STARTS IN ".length)}`;
    }
    if (/^in\s+/i.test(trimmed)) {
        return trimmed.replace(/^in\s+/i, "in ");
    }
    if (trimmed.toUpperCase() === "STARTING SOON") return "Starting soon";
    return formatCompactMatchCardStatusText(trimmed);
}

/** Cricket finished fixtures — Chakra 12 / 100%, #32F78D, sentence case (Figma). */
export const CRICKET_MATCH_FINISHED_STATUS_LABEL = "Finished";

export const CRICKET_MATCH_FINISHED_STATUS_TEXT_CLASS =
    "align-middle font-chakra text-[12px] font-normal not-italic normal-case leading-[100%] tracking-[0] text-[#32F78D] [leading-trim:none]";

export const CRICKET_MATCH_FINISHED_STATUS_CLASS =
    `whitespace-nowrap text-right ${CRICKET_MATCH_FINISHED_STATUS_TEXT_CLASS}`;

/** Match result comment — same tokens as finished status (Figma). */
export const CRICKET_MATCH_RESULT_COMMENT_CLASS = CRICKET_MATCH_FINISHED_STATUS_TEXT_CLASS;

/** Sentence case for ALL CAPS feed labels; mixed-case comments are left as-is. */
export function formatCricketSentenceCase(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    if (trimmed !== trimmed.toUpperCase()) return trimmed;
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function titleCaseWord(word: string): string {
    if (!word) return word;
    return word
        .split("-")
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part))
        .join("-");
}

/** Title-case competition / tournament labels from feed data (any uniform casing → readable). */
export function formatCompetitionDisplayName(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    const hasMixedCase = trimmed !== trimmed.toUpperCase() && trimmed !== trimmed.toLowerCase();
    if (hasMixedCase) return trimmed;
    return trimmed
        .toLowerCase()
        .split(/\s+/)
        .map(titleCaseWord)
        .join(" ");
}

export const MATCH_CARD_COMPACT_TEAM_ROW_CLASS =
    "flex h-[60px] min-h-[60px] max-h-[60px] items-center justify-between bg-white pl-0 pr-2.5";

/** 60×60 crest slot — 48×48 logo centred inside (Figma). */
export const MATCH_CARD_COMPACT_LOGO_BOX_CLASS =
    "flex h-[60px] w-[60px] min-h-[60px] min-w-[60px] flex-shrink-0 items-center justify-center";

export const MATCH_CARD_COMPACT_LOGO_SIZE = "48px";

export const MATCH_CARD_COMPACT_TEAM_NAME_CLASS =
    "min-w-0 flex-1 truncate align-middle font-anton text-[20px] font-normal not-italic uppercase leading-[100%] tracking-[0] [leading-trim:none] text-main";

export const MATCH_CARD_COMPACT_SCORE_CLASS =
    "min-w-[36px] text-right align-middle font-chakra text-[18px] font-normal not-italic leading-[100%] tracking-[0] [leading-trim:none] text-main";

export const MATCH_CARD_COMPACT_SCORE_MUTED_CLASS =
    "text-right font-chakra text-[11px] font-normal leading-[100%] text-main/60";

export const MATCH_CARD_COMPACT_FOOTER_ROW_CLASS =
    "flex h-[60px] min-h-[60px] max-h-[60px] items-stretch border-t border-main60";

export const MATCH_CARD_COMPACT_FOOTER_PLAY_ROW_CLASS =
    "flex min-w-0 flex-1 items-center gap-1 overflow-x-auto hide-scrollbar py-0 pl-2.5 pr-0.5";

export const MATCH_CARD_COMPACT_PLAY_LABEL_CLASS =
    "mr-1.5 shrink-0 align-middle font-anton text-[11px] font-normal not-italic uppercase leading-[9px] tracking-[0] [leading-trim:none] text-white/85";

export const MATCH_CARD_COMPACT_GAME_CHIP_CLASS =
    "flex h-[40px] min-h-[40px] max-h-[40px] min-w-0 flex-shrink-0 items-center gap-1 bg-[#1B062F]/40 px-2 transition-colors hover:bg-[#1B062F]/55";

/** Icon-only game chip when the footer is crowded (many offers / narrow card). */
export const MATCH_CARD_COMPACT_GAME_CHIP_ICON_ONLY_CLASS =
    "flex h-[40px] w-[40px] min-h-[40px] max-h-[40px] min-w-[40px] max-w-[40px] flex-shrink-0 items-center justify-center bg-[#1B062F]/40 transition-colors hover:bg-[#1B062F]/55";

export const MATCH_CARD_COMPACT_GAME_CHIP_LABEL_CLASS =
    "align-middle font-anton text-[11px] font-normal not-italic uppercase leading-[9px] tracking-[0] [leading-trim:none] text-white";

export const MATCH_CARD_COMPACT_GAME_ICON_CLASS =
    "h-[30px] w-[30px] min-h-[30px] min-w-[30px] max-h-[30px] max-w-[30px] shrink-0 object-contain";

export const MATCH_CARD_COMPACT_WATCH_LIVE_CLASS =
    "flex h-[26px] min-h-[26px] max-h-[26px] flex-shrink-0 items-center border border-pink bg-transparent px-1.5 font-anton text-[11px] font-normal uppercase leading-[9px] tracking-normal text-pink transition-opacity hover:opacity-90";

export const MATCH_CARD_COMPACT_WATCH_LIVE_ICON_ONLY_CLASS =
    "flex h-[40px] w-[40px] min-h-[40px] max-h-[40px] min-w-[40px] max-w-[40px] flex-shrink-0 items-center justify-center border border-pink bg-transparent text-pink transition-opacity hover:opacity-90";

/** Header WATCH LIVE CTA — right of IN PLAY (Figma). */
export const MATCH_CARD_COMPACT_WATCH_LIVE_HEADER_CLASS =
    "box-border flex h-[26px] min-h-[26px] max-h-[26px] shrink-0 items-center justify-center bg-[#F73261] px-1.5 text-center align-middle font-anton text-[11px] font-normal not-italic uppercase leading-[9px] tracking-[0] text-white transition-opacity hover:opacity-90";

export const MATCH_CARD_COMPACT_MATCH_CTA_WRAP_CLASS =
    "flex min-w-[60px] flex-shrink-0 flex-col items-center justify-center self-stretch pl-0 pr-2";

/** Compact match card footer MATCH CTA label — Anton 16 / 18 lh (Figma). */
export const MATCH_CARD_COMPACT_MATCH_CTA_LABEL_CLASS =
    "text-center align-middle font-anton text-[16px] font-normal not-italic uppercase leading-[18px] tracking-[0] [leading-trim:none] text-main";

export const MATCH_CARD_COMPACT_MATCH_CTA_SIZE_CLASS =
    "box-border flex h-[40px] w-[60px] min-h-[40px] max-h-[40px] min-w-[60px] max-w-[60px] shrink-0 items-center justify-center p-0";

export const MATCH_CARD_COMPACT_MATCH_CTA_CLASS = `${MATCH_CARD_COMPACT_MATCH_CTA_SIZE_CLASS} bg-lime transition-colors hover:bg-limehover ${MATCH_CARD_COMPACT_MATCH_CTA_LABEL_CLASS}`;

export const MATCH_CARD_COMPACT_HORSE_RACE_DETAILS_WRAP_CLASS =
    "flex min-w-0 max-w-[min(52vw,220px)] flex-shrink items-center self-stretch pr-1";

export const MATCH_CARD_COMPACT_HORSE_RACE_DETAILS_TEXT_CLASS =
    "min-w-0 truncate font-chakra text-[10px] font-normal normal-case leading-[100%] tracking-normal text-white/75";

/** Finished cricket result comment — left of MATCH CTA in compact footer. */
export const MATCH_CARD_COMPACT_RESULT_COMMENT_WRAP_CLASS =
    "flex min-w-0 flex-1 items-center self-stretch overflow-hidden pl-2.5 pr-1";

/** Slightly taller than 100% line-height so Chakra descenders (y, g) are not clipped by truncate. */
export const MATCH_CARD_COMPACT_RESULT_COMMENT_TEXT_CLASS =
    "min-w-0 truncate text-left align-middle font-chakra text-[12px] font-normal not-italic normal-case leading-[16px] tracking-[0] text-[#32F78D]";
